import React, { useState, useEffect, useRef } from 'react';
import {
    Col,
    Dropdown,
    DropdownMenu,
    DropdownToggle,
    Nav,
    NavItem,
    NavLink,
    Row,
    TabContent,
    TabPane,
    Badge
} from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import classnames from 'classnames';
import axios from 'axios';
import SimpleBar from "simplebar-react";
import { useAuth } from "../../Provider/AuthProvider";

// Define the notification interface based on your API response
interface Notification {
    id: string;
    isread: string;
    touser: string;
    touser_fullname: string;
    fromuser: string;
    fromuser_fullname: string;
    text: string;
    pushdate: string;
    liveexpiry: string;
    type: string;
    pushed: string;
    url: string;
    element_id: string;
}

interface NotificationsResponse {
    success: boolean;
    message: string;
    data: Notification[];
    count: number;
    userid: string;
    entity: number;
    entityname: string;
    count_to_valid: number;
}

const NotificationDropdown = () => {
    const { user } = useAuth();
    const navigate = useNavigate();

    const [isNotificationDropdown, setIsNotificationDropdown] = useState<boolean>(false);
    const toggleNotificationDropdown = () => {
        setIsNotificationDropdown(!isNotificationDropdown);
        if (!isNotificationDropdown) {
            fetchAllNotifications();
        }
    };

    //Tab 
    const [activeTab, setActiveTab] = useState<string>('1');
    const toggleTab = (tab: any) => {
        if (activeTab !== tab) {
            setActiveTab(tab);
        }
    };

    // Notifications state
    const [allNotifications, setAllNotifications] = useState<Notification[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [unreadCount, setUnreadCount] = useState<number>(0);

    // Toast notification state
    const [showToast, setShowToast] = useState<boolean>(false);
    const [toastMessage, setToastMessage] = useState<string>('');
const [toastType, setToastType] = useState<'info' | 'danger' | 'warning'>('danger');

    // Ref to store notification interval
    const notificationIntervalRef = useRef<NodeJS.Timeout | null>(null);

    const fetchAllNotifications = async () => {
        if (!user?.token) return;
        setLoading(true);
        try {
            const readResponse: any = await axios.post(
                'https://api.apapiecesauto.fr/apiV2/',
                {
                    action: 'getnotifs',
                    entity: user.entity,
                    isread: "1"
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${user.token}`,
                    }
                }
            );
            // Fetch unread notifications
            const unreadResponse: any = await axios.post(
                'https://api.apapiecesauto.fr/apiV2/',
                {
                    action: 'getnotifs',
                    entity: user.entity,
                    isread: "0"
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${user.token}`,
                    }
                }
            );

            const readNotificationsData: NotificationsResponse = readResponse;
            const unreadNotificationsData: NotificationsResponse = unreadResponse;

            // Combine both read and unread notifications
            const combinedNotifications = [
                ...(unreadNotificationsData.data || []),
                ...(readNotificationsData.data || [])
            ];

            // Sort by pushdate (newest first)
            combinedNotifications.sort((a, b) =>
                new Date(b.pushdate).getTime() - new Date(a.pushdate).getTime()
            );

            setAllNotifications(combinedNotifications);

            // Calculate unread count from unread response
            const unreadCount = unreadNotificationsData.data?.length || 0;
            setUnreadCount(unreadCount);

        } catch (error) {
            console.error('Error fetching notifications:', error);
        } finally {
            setLoading(false);
        }
    };

    const showToastNotification = (count: number) => {
    setToastMessage(`Vous avez ${count} commande${count > 1 ? 's' : ''} a valider`);
    setToastType('danger'); 
    setShowToast(true);

    setTimeout(() => {
        setShowToast(false);
    }, 5000);
};

     const handleVoirDetails = () => {
  
  navigate('/commandes', {
    state: {
      validatedWithExpeditions: true  
    }
  });
};
    // Function to check and notify about unread notifications
    const checkAndNotifyUnread = async () => {
        
        try {
            // Fetch only unread notifications
            const unreadResponse: any = await axios.post(
                'https://api.apapiecesauto.fr/apiV2/',
                {
                    action: 'getnotifs',
                    entity: user.entity,
                    isread: "0"
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${user.token}`,
                    }
                }
            );

            const unreadNotificationsData: NotificationsResponse = unreadResponse;
            const currentUnreadCount = unreadNotificationsData?.count_to_valid || 0;


            // Update unread count
            setUnreadCount(currentUnreadCount);

            // ALWAYS show toast if there's at least 1 unread notification
            if (currentUnreadCount > 0) {
                showToastNotification(currentUnreadCount);
            }

        } catch (error) {
            console.error('Error checking unread notifications:', error);
        }
    };

    const markAsRead = async (notificationId: string) => {
        if (!user?.token) return;

        try {
            await axios.post(
                'https://api.apapiecesauto.fr/apiV2/',
                {
                    action: 'setnotifread',
                    id: notificationId,
                    entity: user.entity
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${user.token}`,
                    }
                }
            );
            setAllNotifications(prev =>
                prev.map(notif =>
                    notif.id === notificationId ? { ...notif, isread: "1" } : notif
                )
            );

            const newUnreadCount = Math.max(0, unreadCount - 1);
            setUnreadCount(newUnreadCount);
        } catch (error) {
            console.error('Error marking notification as read:', error);
        }
    };

    const markAllAsRead = async () => {
        if (!user?.token) return;

        try {
            const unreadNotificationIds = allNotifications
                .filter(notif => notif.isread === "0")
                .map(notif => notif.id);

            const markReadPromises = unreadNotificationIds.map(notificationId =>
                axios.post(
                    'https://api.apapiecesauto.fr/apiV2/',
                    {
                        action: 'setnotifread',
                        id: notificationId,
                        entity: user.entity
                    },
                    {
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${user.token}`,
                        }
                    }
                )
            );

            // Wait for all requests to complete
            await Promise.all(markReadPromises);

            // Update local state - mark all as read
            setAllNotifications(prev =>
                prev.map(notif => ({ ...notif, isread: "1" }))
            );
            setUnreadCount(0);
        } catch (error) {
            console.error('Error marking all notifications as read:', error);
        }
    };

    // Handle notification click
    const handleNotificationClick = (notification: Notification) => {
        // Only mark as read if it's unread
        if (notification.isread === "0") {
            markAsRead(notification.id);
        }

        // Close the dropdown
        setIsNotificationDropdown(false);

        // Handle navigation based on URL
        if (notification.url === "commandes" && notification.element_id) {
            // Navigate to commandes page and store the order ID to open modal
            navigate('/commandes', {
                state: {
                    openOrderModal: true,
                    orderId: notification.element_id
                }
            });
        } else if (notification.url) {
            // Navigate to other URLs normally
            navigate(`/${notification.url}`);
        }
    };

    // Format date to relative time
    const formatRelativeTime = (dateString: string) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

        if (diffInSeconds < 60) return 'À l\'instant';
        if (diffInSeconds < 3600) return `Il y a ${Math.floor(diffInSeconds / 60)} min`;
        if (diffInSeconds < 86400) return `Il y a ${Math.floor(diffInSeconds / 3600)} h`;
        if (diffInSeconds < 2592000) return `Il y a ${Math.floor(diffInSeconds / 86400)} j`;
        return date.toLocaleDateString('fr-FR');
    };

    // Get badge color based on notification type
    const getBadgeColor = (type: string) => {
        switch (type) {
            case 'warning': return 'warning';
            case 'danger': return 'danger';
            case 'success': return 'success';
            case 'info':
            default: return 'info';
        }
    };

    // Get icon based on notification type
    const getNotificationIcon = (type: string) => {
        switch (type) {
            case 'warning': return 'bx bx-error';
            case 'danger': return 'bx bx-x-circle';
            case 'success': return 'bx bx-check-circle';
            case 'info':
            default: return 'bx bx-info-circle';
        }
    };

    // Clean notification text by removing HTML tags and "||" separators
    const cleanNotificationText = (text: string) => {
        // Remove HTML tags but keep the text content
        const cleanText = text.replace(/<[^>]*>/g, '').replace(/\|\|/g, ' - ');
        return cleanText;
    };

    useEffect(() => {
        // Fetch notifications on component mount
        if (user?.token) {
            fetchAllNotifications();

            // Set up interval to refresh notifications every 30 seconds
            const refreshInterval = setInterval(fetchAllNotifications, 30000);

            // Set up interval to check and notify about unread notifications every 3 minutes
            notificationIntervalRef.current = setInterval(() => {
                checkAndNotifyUnread();
            }, 1 * 60 * 1000); // 3 minutes in milliseconds

            // Cleanup intervals on unmount
            return () => {
                clearInterval(refreshInterval);
                if (notificationIntervalRef.current) {
                    clearInterval(notificationIntervalRef.current);
                }
            };
        }
    }, [user]);

    // Auto-hide toast
    useEffect(() => {
        if (showToast) {
            const timer = setTimeout(() => {
                setShowToast(false);
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [showToast]);

    return (
        <React.Fragment>
            {/* Toast Notification */}
           {showToast && (
    <div
        className="toast-notification"
        style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            zIndex: 9999,
            minWidth: '300px',
            backgroundColor: '#dc3545', // Danger red
            color: 'white',
            padding: '12px 20px',
            borderRadius: '4px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            animation: 'slideInRight 0.3s ease',
            borderLeft: '4px solid #a71d2a'
        }}
    >
        <div style={{ display: 'flex', alignItems: 'center' }}>
            <i
                className="ri-error-warning-line me-2"
                style={{ fontSize: '20px' }}
            ></i>
            <span style={{ fontWeight: '500' }}>{toastMessage}</span>
            <button
                onClick={() => setShowToast(false)}
                style={{
                    background: 'none',
                    border: 'none',
                    color: 'white',
                    marginLeft: 'auto',
                    cursor: 'pointer',
                    fontSize: '18px',
                    fontWeight: 'bold'
                }}
            >
                ×
            </button>
        </div>
        <button style={{ 
            marginTop: '8px', 
            fontSize: '12px', 
           
            
        }}
        onClick={handleVoirDetails}>
           voir les détails
        </button>
    </div>
)}

            {/* Notification Dropdown */}
            <Dropdown isOpen={isNotificationDropdown} toggle={toggleNotificationDropdown} className="topbar-head-dropdown ms-1 header-item">
                <DropdownToggle type="button" tag="button" className="btn btn-icon btn-topbar btn-ghost-secondary rounded-circle">
                    <i className='bx bx-bell fs-22'></i>
                    {unreadCount > 0 && (
                        <span className="position-absolute topbar-badge fs-10 translate-middle badge rounded-pill bg-danger">
                            {unreadCount > 99 ? '99+' : unreadCount}
                            <span className="visually-hidden">unread messages</span>
                        </span>
                    )}
                </DropdownToggle>
                <DropdownMenu className="dropdown-menu-lg dropdown-menu-end p-0">
                    <div className="dropdown-head bg-secondary bg-pattern rounded-top">
                        <div className="px-2 pt-2">
                            <Nav className="nav-tabs dropdown-tabs nav-tabs-custom">
                                <NavItem>
                                    <NavLink
                                        href="#"
                                        className={classnames({ active: activeTab === '1' })}
                                        onClick={() => { toggleTab('1'); }}
                                    >
                                        Toutes ({allNotifications.length})
                                    </NavLink>
                                </NavItem>
                                <NavItem>
                                    <NavLink
                                        href="#"
                                        className={classnames({ active: activeTab === '2' })}
                                        onClick={() => { toggleTab('2'); }}
                                    >
                                        Non lues ({unreadCount})
                                    </NavLink>
                                </NavItem>
                            </Nav>
                        </div>
                    </div>

                    <TabContent activeTab={activeTab}>
                        <TabPane tabId="1" className="py-2 ps-2">
                            <SimpleBar style={{ maxHeight: "300px" }} className="pe-2">
                                {loading ? (
                                    <div className="text-center py-4">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Chargement...</span>
                                        </div>
                                    </div>
                                ) : allNotifications.length > 0 ? (
                                    allNotifications.map((notification) => (
                                        <div
                                            key={notification.id}
                                            className={`text-reset notification-item d-block dropdown-item position-relative ${notification.isread === "0" ? 'unread-notification' : ''}`}
                                            style={{
                                                cursor: 'pointer',
                                                backgroundColor: notification.isread === "0" ? '#f8f9fa' : 'transparent',
                                                borderLeft: notification.isread === "0" ? '3px solid #007bff' : '3px solid transparent'
                                            }}
                                            onClick={() => handleNotificationClick(notification)}
                                        >
                                            <div className="d-flex">
                                                <div className="me-3">
                                                    <span className={`text-${getBadgeColor(notification.type)} fs-16`}>
                                                        <i className={getNotificationIcon(notification.type)}></i>
                                                    </span>
                                                </div>
                                                <div className="flex-grow-1">
                                                    <h6 className="mt-0 mb-2 lh-base">
                                                        {cleanNotificationText(notification.text)}
                                                    </h6>
                                                    <p className="mb-0 fs-11 fw-medium text-uppercase text-muted">
                                                        <span><i className="mdi mdi-clock-outline"></i> {formatRelativeTime(notification.pushdate)}</span>
                                                    </p>
                                                    {notification.fromuser_fullname && (
                                                        <small className="text-muted">De: {notification.fromuser_fullname}</small>
                                                    )}
                                                </div>
                                                <div className="px-2 fs-15">
                                                    {notification.isread === "0" && (
                                                        <Badge color="primary" pill>Nouveau</Badge>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="text-center py-4">
                                        <i className="bx bx-bell-off display-5 text-muted"></i>
                                        <h6 className="fs-18 fw-semibold lh-base mt-2">Aucune notification</h6>
                                        <p className="text-muted mb-0">Vous n'avez aucune notification pour le moment.</p>
                                    </div>
                                )}

                                {allNotifications.length > 0 && unreadCount > 0 && (
                                    <div className="my-3 text-center">
                                        <button
                                            type="button"
                                            className="btn btn-soft-success waves-effect waves-light"
                                            onClick={markAllAsRead}
                                        >
                                            Tout marquer comme lu <i className="ri-check-double-line align-middle"></i>
                                        </button>
                                    </div>
                                )}
                            </SimpleBar>
                        </TabPane>

                        <TabPane tabId="2" className="py-2 ps-2">
                            <SimpleBar style={{ maxHeight: "300px" }} className="pe-2">
                                {loading ? (
                                    <div className="text-center py-4">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Chargement...</span>
                                        </div>
                                    </div>
                                ) : unreadCount > 0 ? (
                                    allNotifications
                                        .filter(notification => notification.isread === "0")
                                        .map((notification) => (
                                            <div
                                                key={notification.id}
                                                className="text-reset notification-item d-block dropdown-item position-relative"
                                                style={{
                                                    cursor: 'pointer',
                                                    backgroundColor: '#f8f9fa',
                                                    borderLeft: '3px solid #007bff'
                                                }}
                                                onClick={() => handleNotificationClick(notification)}
                                            >
                                                <div className="d-flex">
                                                    <div className="me-3">
                                                        <span className={`text-${getBadgeColor(notification.type)} fs-16`}>
                                                            <i className={getNotificationIcon(notification.type)}></i>
                                                        </span>
                                                    </div>
                                                    <div className="flex-grow-1">
                                                        <h6 className="mt-0 mb-2 lh-base">
                                                            {cleanNotificationText(notification.text)}
                                                        </h6>
                                                        <p className="mb-0 fs-11 fw-medium text-uppercase text-muted">
                                                            <span><i className="mdi mdi-clock-outline"></i> {formatRelativeTime(notification.pushdate)}</span>
                                                        </p>
                                                        {notification.fromuser_fullname && (
                                                            <small className="text-muted">De: {notification.fromuser_fullname}</small>
                                                        )}
                                                    </div>
                                                    <div className="px-2 fs-15">
                                                        <Badge color="primary" pill>Nouveau</Badge>
                                                    </div>
                                                </div>
                                            </div>
                                        ))
                                ) : (
                                    <div className="text-center py-4">
                                        <i className="bx bx-check-double display-5 text-muted"></i>
                                        <h6 className="fs-18 fw-semibold lh-base mt-2">Tout est lu</h6>
                                        <p className="text-muted mb-0">Vous n'avez aucune notification non lue.</p>
                                    </div>
                                )}
                            </SimpleBar>
                        </TabPane>
                    </TabContent>
                </DropdownMenu>
            </Dropdown>
        </React.Fragment>
    );
};

export default NotificationDropdown;
